@file:Suppress("UNCHECKED_CAST_TO_EXTERNAL_INTERFACE")

package org.example.project.screens_cus

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import kotlinx.coroutines.launch
import org.example.project.dashboard.LocalScreenRefreshVersion
import org.example.project.enginekartei.KarteiApi
import org.example.project.enginetabular.TabularScreen
import org.example.project.service.GlobalManager
import org.example.project.service.InfrastrukturApi
import org.example.project.service.YardApi
import org.example.project.settings.AppDropdown
import org.example.project.settings.AppDropdownAppearance
import org.example.project.settings.AppDropdownMenuMode
import org.example.project.utils.All
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Text

private const val CHECKED = "\u2705"
private const val UNCHECKED = "\u2B1C"
private const val WE_TORFREIGABE_PREFIX = "we.torfreigabe.mandant."

private data class WeTorfreigabeMandantColumn(
    val key: String,
    val label: String
)

@Composable
fun CUSWETorfreigabeScreen() {
    val scope = rememberCoroutineScope()
    var tore by remember { mutableStateOf<List<All>>(emptyList()) }
    var standorte by remember { mutableStateOf<List<All>>(emptyList()) }
    var selectedStandort by remember { mutableStateOf("") }
    var mandanten by remember { mutableStateOf<List<All>>(emptyList()) }
    var freigaben by remember { mutableStateOf<List<All>>(emptyList()) }
    var loading by remember { mutableStateOf(true) }
    val refreshVersion = maxOf(
        LocalScreenRefreshVersion.current,
        GlobalManager.jsonDomainVersion("yard"),
        GlobalManager.jsonDomainVersion("company"),
        GlobalManager.jsonDomainVersion("mandant")
    )

    suspend fun ladeDaten() {
        loading = true
        standorte = KarteiApi.loadStandorte()
            .sortedBy { it.id.toIntOrNull() ?: Int.MAX_VALUE }
        tore = emptyList()
        mandanten = KarteiApi.loadMandanten()
            .filter { weMandantKey(it).isNotBlank() }
            .distinctBy { weMandantKey(it) }
            .sortedBy { weMandantLabel(it).lowercase() }
        freigaben = emptyList()
        loading = false
    }

    LaunchedEffect(refreshVersion) {
        ladeDaten()
    }

    val standortIds = remember(tore) {
        tore.map { it.standort.trim() }
            .filter { it.isNotBlank() }
            .distinct()
            .sortedWith(compareBy<String> { it.toIntOrNull() ?: Int.MAX_VALUE }.thenBy { it })
    }

    LaunchedEffect(standortIds) {
        if (selectedStandort.isNotBlank() && selectedStandort !in standortIds) {
            selectedStandort = ""
        }
    }

    if (loading) {
        Div({
            style {
                height(100.percent)
                display(DisplayStyle.Flex)
                alignItems(org.jetbrains.compose.web.css.AlignItems.Center)
                justifyContent(org.jetbrains.compose.web.css.JustifyContent.Center)
                color(Color.white)
                fontSize(15.px)
            }
        }) { Text("Lade WE-Torfreigaben ...") }
        return
    }

    val mandantColumns = remember(mandanten) {
        mandanten.toWeTorfreigabeColumns()
    }
    val freigabenByTorcode = remember(freigaben) {
        freigaben.associateBy { it.torcode.orEmpty().ifBlank { it.id } }
    }
    val headers = remember(mandantColumns) {
        listOf("ID", "Standort", "Halle", "Tor") + mandantColumns.map { it.label }
    }
    val sichtbareTore = remember(tore, selectedStandort) {
        tore.filter { selectedStandort.isBlank() || it.standort == selectedStandort }
    }
    val standortOptionen = remember(standortIds, standorte) {
        buildList {
            add(All(key = "", label = "Alle Standorte"))
            addAll(standortIds.map { id ->
                val standort = standorte.firstOrNull { it.id == id }
                All(
                    key = id,
                    label = standort?.standort?.takeIf { it.isNotBlank() } ?: "Standort $id"
                )
            })
        }
    }
    val data = remember(sichtbareTore, freigabenByTorcode, mandantColumns) {
        sichtbareTore.map { tor ->
            val rowId = weTorRowId(tor)
            val saved = freigabenByTorcode[rowId]
            val row = buildList {
                add(rowId)
                add(tor.standort)
                add(tor.halle)
                add(tor.tor?.toString().orEmpty())
                mandantColumns.forEach { column ->
                    add(if (saved?.palettenWerte?.get(weFreigabeExtraKey(column.key)) == "true") CHECKED else UNCHECKED)
                }
            }
            rowId to row
        }
    }

    TabularScreen(
        screenKey = "WETorfreigabeScreen",
        rollenKey = "WETorfreigabeScreen",
        title = "WE Torfreigabe",
        headers = headers,
        data = data,
        editable = true,
        allowCreate = false,
        allowDelete = false,
        nonEditableCols = setOf("ID", "Standort", "Halle", "Tor"),
        toggleColumns = mandantColumns.map { it.label }.toSet(),
        extraButtons = {
            Div({ style { width(240.px) } }) {
                AppDropdown(
                    options = standortOptionen,
                    selectedValue = selectedStandort,
                    onChange = { selectedStandort = it },
                    appearance = AppDropdownAppearance.Field,
                    menuMode = AppDropdownMenuMode.Overlay
                )
            }
        },
        onSearchWithFilters = { filters ->
            scope.launch {
                val rows = YardApi.searchWeTorfreigaben(
                    filters,
                    mandantColumns.associate { it.label to it.key }
                )
                tore = rows
                freigaben = rows
            }
        },
        onCommit = { rows ->
            scope.launch {
                rows.forEach { (id, cols) ->
                    val tor = tore.firstOrNull { weTorRowId(it) == id } ?: return@forEach
                    val existing = freigabenByTorcode[id]
                    val freigabeWerte = mandantColumns.mapIndexed { index, column ->
                        weFreigabeExtraKey(column.key) to (cols.getOrNull(index + 4) == CHECKED).toString()
                    }.toMap()
                    val updated = (existing ?: All()).copy(
                        id = id,
                        typ = "WE_TORFREIGABE",
                        auftragsart = "WE",
                        standort = tor.standort,
                        halle = tor.halle,
                        tor = tor.tor,
                        torcode = tor.torcode,
                        palettenWerte = existing?.palettenWerte.orEmpty()
                            .filterKeys { !it.startsWith(WE_TORFREIGABE_PREFIX) } + freigabeWerte
                    )
                    YardApi.saveWeTorfreigabe(updated)
                }
                ladeDaten()
            }
        }
    )
}

private fun weTorRowId(tor: All): String =
    tor.torcode.orEmpty().ifBlank {
        tor.id.ifBlank {
        listOf(tor.standort, tor.halle, tor.tor?.toString().orEmpty())
            .joinToString("-")
            .ifBlank { "tor" }
        }
    }

private fun weMandantKey(mandant: All): String =
    mandant.firmenId.ifBlank { mandant.id }.trim()

private fun weMandantLabel(mandant: All): String =
    mandant.firma
        .ifBlank { mandant.kurzbezeichnung }
        .ifBlank { mandant.name }
        .ifBlank { mandant.firmenId }
        .ifBlank { mandant.id }
        .trim()

private fun weFreigabeExtraKey(mandantKey: String): String =
    WE_TORFREIGABE_PREFIX + mandantKey

private fun List<All>.toWeTorfreigabeColumns(): List<WeTorfreigabeMandantColumn> {
    val labelCounts = mutableMapOf<String, Int>()
    return map { mandant ->
        val baseLabel = weMandantLabel(mandant).ifBlank { weMandantKey(mandant) }
        val count = (labelCounts[baseLabel] ?: 0) + 1
        labelCounts[baseLabel] = count
        WeTorfreigabeMandantColumn(
            key = weMandantKey(mandant),
            label = if (count == 1) baseLabel else "$baseLabel ($count)"
        )
    }
}
